import React, { ReactNode, isValidElement } from "react";
import { Icon, IconName } from "components/icon/Icon";
import { KebabCase } from "utils/types";
import { cn } from "utils";
import { Card } from "components/ui/card";
import { Tooltip, TooltipContent, TooltipTrigger } from "components/ui/tooltip";
import { Badge, BadgeVariant } from "components/ui/badge";
import { Text } from "components/text";
import { TruncatedText } from "components/TruncatedText/TruncatedText";
import { ActionCardAction, ActionCardActionProps } from "./ActionCardAction";
import { ActionCardDropdown } from "./ActionCardDropdown";

interface ActionCardProps {
  // Card content
  title: string;
  subtitle?: string;
  iconName: KebabCase<IconName>;
  iconWrapperClassName?: string;
  iconClassName?: string;

  // Loading state
  isLoading?: boolean;

  // Badge
  badge?: {
    text: string;
    variant?: BadgeVariant;
    className?: string;
    tooltip?: string;
  };

  // Accessibility & testing
  ariaLabel?: string;
  testId?: string;

  // Actions - compound component pattern only
  children?: ReactNode;
  actionOnHover?: boolean;
  forceDropdown?: boolean; // Force dropdown even for 1-2 actions

  // Event handlers
  onClick?: () => void;
}

export const ActionCard = ({
  // Card content
  title,
  subtitle,
  iconName,
  iconWrapperClassName,
  iconClassName,

  // Loading state
  isLoading,

  // Badge
  badge,

  // Accessibility & testing
  ariaLabel,
  testId = "content-card",

  // Actions
  children,
  actionOnHover = false,
  forceDropdown = false,

  // Event handlers
  onClick,
}: ActionCardProps) => {
  // compound-component API needs to inspect children by element type
  const childrenArray = React.Children.toArray(children);
  const otherChildren = childrenArray.filter(
    (child) => isValidElement(child) && child.type !== ActionCardAction,
  );
  const actions = childrenArray.filter(
    (child) => isValidElement(child) && child.type === ActionCardAction,
  );

  // Extract valid actions from children
  if (process.env.NODE_ENV === "development") {
    if (actions.length > 0 && otherChildren.length > 0) {
      console.warn(
        "ActionCard: When ActionCard.Action is used, no other child type is supported and will be ignored.",
      );
    }
  }
  const renderActions = () => {
    if (childrenArray.length === 0) return null;

    if (otherChildren.length > 0 && actions.length === 0) {
      return <div className="flex items-center space-x-2">{otherChildren}</div>;
    }

    // Force dropdown if requested or more than 2 actions
    if (forceDropdown || actions.length > 2) {
      return (
        <ActionCardDropdown actions={actions} actionOnHover={actionOnHover} />
      );
    }

    // Show inline for 1-2 actions
    return (
      <div className="flex items-center space-x-2">
        {actions.map((child) => {
          if (isValidElement(child) && child.type === ActionCardAction) {
            // injects actionOnHover into ActionCard.Action children
            return React.cloneElement(child, {
              ...(child.props as ActionCardActionProps),
              actionOnHover,
            } as ActionCardActionProps);
          }
          return null;
        })}
      </div>
    );
  };

  return (
    <Card
      className={cn(
        "border-secondary-50 border shadow-xs rounded-xl bg-white",
        "flex flex-row items-center justify-between gap-8 px-6 py-3 h-[90px]",
        actionOnHover && "group",
        onClick && "cursor-pointer",
        "[&:not(:first-child)]:rounded-t-none",
        "[&:not(:last-child)]:rounded-b-none",
        // optional: remove double border between adjacent cards
        "[&:not(:first-child)]:-mt-px",
      )}
      onClick={onClick}
      tabIndex={onClick ? 0 : undefined}
      role={onClick ? "button" : undefined}
      aria-label={ariaLabel}
      data-testid={testId}
    >
      <div className="flex items-center space-x-4 min-w-0 flex-1">
        <div
          className={cn(
            "rounded-full h-12 w-12 shrink-0 flex items-center justify-center bg-secondary-25",
            iconWrapperClassName,
          )}
        >
          <Icon
            name={iconName}
            iconClassName={cn("w-5! h-5!", iconClassName)}
          />
        </div>

        <div className="space-y-0.5 min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <div className="text-secondary-900  truncate overflow-hidden">
              {isLoading ? (
                <div className="bg-secondary-50 rounded-md animate-pulse h-5 w-3/4">
                  &nbsp;
                </div>
              ) : (
                <TruncatedText text={title} className="" />
              )}
            </div>
            {badge && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Badge
                      variant={badge.variant}
                      className={cn("text-xs", badge.className)}
                    >
                      {badge.text}
                    </Badge>
                  </div>
                </TooltipTrigger>
                {badge.tooltip && (
                  <TooltipContent side="top" align="end">
                    <Text variant="caption" className="max-w-xs">
                      {badge.tooltip}
                    </Text>
                  </TooltipContent>
                )}
              </Tooltip>
            )}
          </div>
          {subtitle && (
            <div className="text-sm text-secondary-400 space-x-2">
              <TruncatedText text={subtitle} />
            </div>
          )}
        </div>
      </div>

      {renderActions()}
    </Card>
  );
};

// Export compound component
ActionCard.Action = ActionCardAction;
